import React, { useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { Lock, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import Seo from "../components/Seo";

export default function AdminLogin() {
  const { authed, checking, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  if (checking) {
    return <div className="pt-40 text-center font-display text-3xl">Verifying session…</div>;
  }
  if (authed) {
    const from = location.state?.from?.pathname || "/admin";
    return <Navigate to={from} replace />;
  }

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await login(password);
      toast.success("Welcome back.");
      navigate(location.state?.from?.pathname || "/admin", { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : "Invalid password";
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="pt-32 pb-24 min-h-[80vh]" data-testid="admin-login-page">
      <Seo title="Admin sign-in" noindex />
      <div className="max-w-[560px] mx-auto px-6">
        <div className="border border-[var(--mn-ink)] p-10 md:p-14">
          <div className="inline-flex items-center gap-2 text-[var(--mn-ink-2)] text-xs uppercase tracking-[0.32em]">
            <Lock size={14} strokeWidth={1.5} />
            Private Access
          </div>
          <h1 className="font-display text-4xl md:text-5xl mt-4 leading-[1.02] tracking-tighter">
            Welcome <em className="font-medium italic">back.</em>
          </h1>
          <p className="text-[var(--mn-ink-2)] text-sm mt-4 leading-relaxed">
            Enter your password to manage the collection, view orders, and review
            customer enquiries.
          </p>

          <form onSubmit={submit} className="mt-10 space-y-6" data-testid="admin-login-form">
            <label className="block">
              <span className="eyebrow block mb-2">Password</span>
              <input
                autoFocus
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••••••"
                className="field-input"
                data-testid="admin-password-input"
              />
            </label>

            {error && (
              <p className="text-sm text-[var(--mn-accent)] uppercase tracking-[0.18em]" data-testid="admin-login-error">
                — {error}
              </p>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="btn-primary w-full justify-center"
              data-testid="admin-login-submit"
            >
              {submitting ? "Verifying…" : "Sign in"}
              {!submitting && <ArrowRight size={16} strokeWidth={1.5} />}
            </button>
          </form>

          <p className="text-[11px] text-[var(--mn-muted)] uppercase tracking-[0.22em] mt-8">
            5 attempts per minute · Sessions last 30 days
          </p>
        </div>
      </div>
    </div>
  );
}
